// Urun <-> Secenek Kutuphanesi eslestirmesi. Eklenti (katalog'a eklerken),
// panel (katalogda "Otomatik algila") ve taslak kurucusu AYNI kurali kullanir.
//
// Sira:
//   1) HATIRLANAN eslesmeler (kullanicinin bir kez yaptigi): Rexven ekseninin
//      ADI + ILK DEGERI ayni ise ayni set (ya da "ozellik olarak kalsin").
//   2) Cok degerli eksenler: ad ya da deger benzerliginden otomatik eslesme.
//   3) Tek degerli eksenler (Zincir Tipi: "30 Force Chain") URUN OZELLIGIDIR —
//      hatirlanan bir eslesme yoksa secenek yapilmaz.

export type OptionRole = "variation" | "personalization";

/** Urune (katalog urunu) bagli secenek seti */
export interface ProductOptionRef {
  optionSetId: string;
  role: OptionRole;
  required?: boolean;
  /** Yalnizca bu degerler listelenir (bos: setin tum degerleri) */
  values?: string[];
  /** true: set yalnizca CHART gorseli icin bagli (fotograflarin sonuna eklenir);
   *  soru/varyasyon olarak sorulmaz (or. coklu tasli urunde doğum tasi tablosu) */
  chartOnly?: boolean;
}

/** Hatirlanan Rexven ekseni -> kutuphane seti kurali.
 *  optionSetId bos ise: bu eksen OZELLIK olarak kalir (secenek yapilmaz). */
export interface OptionMapRule {
  axisName: string;
  firstValue?: string;
  optionSetId: string;
  role: OptionRole;
  /** Kural hangi urun turu icin (kolye/bileklik...). Doluysa yalnizca o turde
   *  gecerli ve ilk degerden bagimsiz eslesir: zincir uzunlugu kolyede kolye
   *  setine, bileklikte bileklik setine gider. Bos: eski (turden bagimsiz) kural. */
  productTypeKey?: string | null;
}

export interface OptionSetLike {
  id: string;
  name: string;
  etsyName?: string | null;
  kind: string; // list | text | upload | chart
  values: Array<{ label: string }>;
  defaultRole: OptionRole | string;
  productTypeKey?: string | null;
}

export interface OptionMatchProduct {
  name?: string;
  colorAvailability?: Array<{ color: string }>;
  personalization?: { enabled?: boolean } | null;
  axisRoles?: Record<string, string> | null;
  source?: {
    variationTypes?: Array<{ name: string; values: string[] }> | null;
    /** Tek degerli eksenler / sabit ozellikler ("Zincir Uzunlugu": "12 inches") */
    attributes?: Record<string, string> | null;
  } | null;
  /** Fiyat satirlari — "fiyat ekseni" gercekten fiyat degistiriyor mu */
  sizeOptions?: Array<{ cost?: number; costTiers?: { premium?: number; plus?: number; base?: number } | null }> | null;
}

export function normOption(s: string): string {
  return String(s ?? "")
    .toLowerCase()
    .replace(/ı/g, "i").replace(/İ/g, "i")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Iki deger ayni mi: birebir ya da (3+ karakterde) biri digerini iceriyor */
export function optionValueMatch(a: string, b: string): boolean {
  const x = normOption(a), y = normOption(b);
  if (!x || !y) return false;
  if (x === y) return true;
  return x.length >= 3 && y.length >= 3 && (x.includes(y) || y.includes(x));
}

/** Urunun fiyati eksene gore GERCEKTEN degisiyor mu (satirlarin maliyeti farkli) */
export function productPriceVaries(product: Pick<OptionMatchProduct, "sizeOptions">): boolean {
  const costs = new Set((product.sizeOptions ?? [])
    .map((so) => so.costTiers?.premium ?? so.costTiers?.plus ?? so.costTiers?.base ?? so.cost)
    .filter((c): c is number => typeof c === "number")
    .map((c) => c.toFixed(2)));
  return costs.size > 1;
}

const COLOR_AXIS = /\b(renk|color|colour|finish)\b/;

/** Urunun TUM eksenleri: varyasyon eksenleri + sabit ozellikler (tekrarsiz).
 *  Renk ekseni yoksa renk listesi "Renk" ekseni olarak eklenir. */
export function productAxesForMatch(product: OptionMatchProduct): Array<{ name: string; values: string[] }> {
  const out: Array<{ name: string; values: string[] }> = [];
  const seen = new Set<string>();
  for (const a of product.source?.variationTypes ?? []) {
    const k = normOption(a.name);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push({ name: a.name, values: [...new Set((a.values ?? []).map((v) => String(v).trim()).filter(Boolean))] });
  }
  for (const [name, v] of Object.entries(product.source?.attributes ?? {})) {
    const k = normOption(name);
    if (!k || seen.has(k) || !v) continue;
    seen.add(k);
    out.push({ name, values: [String(v)] });
  }
  const colors = (product.colorAvailability ?? []).map((c) => c.color).filter(Boolean);
  const hasColorAxis = out.some((a) => COLOR_AXIS.test(normOption(a.name)));
  if (colors.length > 1 && !hasColorAxis) out.push({ name: "Renk", values: colors });
  return out;
}

/** Eksene uyan hatirlanan kural. Oncelik: ayni tur + ayni ilk deger > ayni tur
 *  > turden bagimsiz kural (ayni ilk deger). Baska ture ait kural hic eslesmez. */
export function findOptionRule(
  rules: OptionMapRule[] | null | undefined,
  axis: { name: string; values: string[] },
  typeKey?: string | null,
): OptionMapRule | undefined {
  const n = normOption(axis.name);
  const first = normOption(axis.values[0] ?? "");
  const t = typeKey ? normOption(typeKey) : "";
  const typeOf = (r: OptionMapRule) => (r.productTypeKey ? normOption(r.productTypeKey) : "");
  const firstOk = (r: OptionMapRule) => !r.firstValue || normOption(r.firstValue) === first;
  const cand = (rules ?? []).filter((r) => normOption(r.axisName) === n);
  return (t ? cand.find((r) => typeOf(r) === t && firstOk(r)) ?? cand.find((r) => typeOf(r) === t) : undefined)
    ?? cand.find((r) => !typeOf(r) && firstOk(r));
}

/** Hatirlanan kuralin benzersiz anahtari (ayni eksen farkli turlerde ayri kural) */
export function optionRuleKey(r: OptionMapRule): string {
  return `${normOption(r.axisName)}|${normOption(r.firstValue ?? "")}|${r.productTypeKey ? normOption(r.productTypeKey) : ""}`;
}

/** Setin, eksenin degerleriyle kesisen degerleri (urune ozel daraltma) */
export function narrowOptionValues(set: OptionSetLike, axisValues: string[]): string[] | undefined {
  if (axisValues.length <= 1) return undefined; // tek deger: setin tamami
  const matched = set.values.filter((v) => axisValues.some((cv) => optionValueMatch(v.label, cv))).map((v) => v.label);
  return matched.length > 0 && matched.length < set.values.length ? matched : undefined;
}

const PERSONAL_TEXT = /\b(isim|name|harf|letter|initial|yazi|text|monogram)\b/;

/** Urun icin onerilen secenek setleri (+ neden, hangi eksenden) */
export function suggestOptionRefs(
  product: OptionMatchProduct,
  sets: OptionSetLike[],
  typeKey?: string | null,
  rules?: OptionMapRule[] | null,
): {
  refs: ProductOptionRef[];
  reasons: Record<string, string>;
  /** set id -> eslestigi urun ekseni */
  axisOf: Record<string, string>;
  /** Kutuphanede karsiligi OLMAYAN cok degerli eksenler — yeni set onerisi */
  unmatched: Array<{ name: string; values: string[]; role: OptionRole }>;
  /** Secenek yapilmayan tek degerli eksenler — urun OZELLIGI (baslik/aciklama) */
  attributes: Array<{ name: string; value: string }>;
} {
  const roles = product.axisRoles ?? {};
  // Fiyat ekseni gercekten fiyat degistirmiyorsa siradan eksendir
  const priceIsReal = productPriceVaries(product) || !product.sizeOptions;
  const isPriceAxis = (name: string) => roles[name] === "price" && priceIsReal;
  const axes = productAxesForMatch(product).filter((a) => !isPriceAxis(a.name));
  const typeOk = (s: OptionSetLike) => !s.productTypeKey || !typeKey || normOption(s.productTypeKey) === normOption(typeKey);

  const refs: ProductOptionRef[] = [];
  const reasons: Record<string, string> = {};
  const axisOf: Record<string, string> = {};
  const handled = new Set<string>(); // eksen adi (norm)
  const used = new Set<string>(); // set id
  const add = (set: OptionSetLike, role: OptionRole, axis: { name: string; values: string[] } | null, reason: string) => {
    if (used.has(set.id)) return;
    used.add(set.id);
    const values = axis ? narrowOptionValues(set, axis.values) : undefined;
    refs.push({ optionSetId: set.id, role, ...(values ? { values } : {}), ...(role === "personalization" && set.kind === "text" ? { required: true } : {}) });
    reasons[set.id] = reason;
    if (axis) { axisOf[set.id] = axis.name; handled.add(normOption(axis.name)); }
  };

  // 1) Hatirlanan eslesmeler (ad + ilk deger)
  for (const axis of axes) {
    const rule = findOptionRule(rules, axis, typeKey);
    if (!rule) continue;
    handled.add(normOption(axis.name));
    if (!rule.optionSetId) continue; // "ozellik olarak kalsin"
    const set = sets.find((s) => s.id === rule.optionSetId);
    if (set) add(set, rule.role, axis, `hatırlanan eşleşme (${axis.name}${axis.values[0] ? `: ${axis.values[0]}` : ""})`);
  }

  // 2) Cok degerli eksenler: ad / deger benzerligi
  const multi = axes.filter((a) => a.values.length > 1);
  for (const set of sets) {
    if (set.kind !== "list" || used.has(set.id) || !typeOk(set)) continue;
    const setNames = [set.name, set.etsyName ?? ""].filter(Boolean);
    let best: { axis: { name: string; values: string[] }; score: number } | null = null;
    for (const a of multi) {
      if (handled.has(normOption(a.name))) continue;
      const byName = setNames.some((n) => optionValueMatch(n, a.name));
      const overlap = set.values.filter((v) => a.values.some((cv) => optionValueMatch(v.label, cv))).length;
      const byValues = overlap >= 2 || (overlap >= 1 && overlap >= Math.ceil(a.values.length / 2));
      if (!byName && !byValues) continue;
      const score = (byName ? 10 : 0) + overlap;
      if (!best || score > best.score) best = { axis: a, score };
    }
    if (!best) continue;
    add(set, set.defaultRole === "personalization" ? "personalization" : "variation", best.axis, `"${best.axis.name}" ekseni (${best.axis.values.length} değer)`);
  }

  // Metin (isim/harf) setleri: urunde kisisellestirme ya da isim ekseni varsa
  for (const set of sets) {
    if (set.kind !== "text" || used.has(set.id) || !typeOk(set)) continue;
    const nameHit = [set.name, set.etsyName ?? ""].some((n) => n && PERSONAL_TEXT.test(normOption(n)));
    if (!nameHit) continue;
    const axisHit = axes.find((a) => PERSONAL_TEXT.test(normOption(a.name)) && !handled.has(normOption(a.name)));
    if (product.personalization?.enabled) add(set, "personalization", null, "üründe isim/harf kişiselleştirmesi var");
    else if (axisHit) add(set, "personalization", axisHit, `"${axisHit.name}" ekseni`);
  }

  // Etsy: fiyat ekseni + en fazla 2 varyasyon. Fazlasi kisisellestirmeye iner.
  let slots = 3 - (Object.keys(roles).some(isPriceAxis) ? 1 : 0);
  for (const r of refs) {
    if (r.role !== "variation") continue;
    if (slots > 0) slots--; else r.role = "personalization";
  }

  // Eslesmeyen cok degerli eksenler: yeni set onerisi
  const unmatched = axes
    .filter((a) => a.values.length > 1 && !handled.has(normOption(a.name)))
    .map((a) => {
      const role: OptionRole = slots > 0 ? "variation" : "personalization";
      if (role === "variation") slots--;
      return { name: a.name, values: a.values, role };
    });

  // Secenek yapilmayan tek degerli eksenler: urun ozelligi
  const mappedAxes = new Set(Object.values(axisOf).map(normOption));
  const attributes = axes
    .filter((a) => a.values.length === 1 && !mappedAxes.has(normOption(a.name)))
    .map((a) => ({ name: a.name, value: a.values[0] }));

  return { refs, reasons, axisOf, unmatched, attributes };
}
